/**
 * 満足度バロメーターの入力コンポーネント
 *
 * 5段階の顔アイコンから選択して、その日の満足度を記録する。
 * 選択済みのアイコンにはハイライトリングを表示する。
 */

import { useState, useCallback, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { daily_satisfactions_path } from '@/lib/routes'

const EMOJIS = [
  { level: 1, emoji: '\u{1F61E}', label: 'とても不満' },
  { level: 2, emoji: '\u{1F615}', label: 'やや不満' },
  { level: 3, emoji: '\u{1F610}', label: 'ふつう' },
  { level: 4, emoji: '\u{1F642}', label: 'やや満足' },
  { level: 5, emoji: '\u{1F60A}', label: 'とても満足' },
] as const

interface SatisfactionPickerProps {
  value: number | null
  today: string
  onChange?: (level: number) => void
}

export function SatisfactionPicker({
  value,
  today,
  onChange,
}: SatisfactionPickerProps) {
  const [selected, setSelected] = useState(value)
  const [pending, setPending] = useState(false)

  // value prop の変更に追随
  useEffect(() => {
    setSelected(value)
  }, [value])

  const handleSelect = useCallback(
    async (level: number) => {
      if (pending) return

      const previousValue = selected
      // 楽観的更新
      setSelected(level)
      setPending(true)
      onChange?.(level)

      try {
        const csrfToken =
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
            ?.content || ''
        const response = await fetch(daily_satisfactions_path(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ date: today, level }),
        })

        if (!response.ok) {
          // エラー時は元に戻す
          setSelected(previousValue)
        }
      } catch {
        setSelected(previousValue)
      } finally {
        setPending(false)
      }
    },
    [pending, selected, today, onChange]
  )

  return (
    <div className="bg-card border-border rounded-xl border p-4">
      <h3 className="text-foreground mb-3 text-sm font-medium">今日の気分</h3>
      <div className="flex justify-around">
        {EMOJIS.map(({ level, emoji, label }) => (
          <button
            key={level}
            onClick={() => handleSelect(level)}
            disabled={pending}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full text-xl transition-all',
              selected === level
                ? 'bg-primary/10 ring-primary ring-2'
                : 'hover:bg-muted',
              pending && 'opacity-50'
            )}
            aria-label={label}
            title={label}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}
